import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone } from '@angular/core';
import {
    BehaviorSubject,
    distinctUntilChanged,
    fromEvent,
    interval,
    map,
    merge,
    Subject,
    Subscription,
    takeUntil,
    throttleTime,
} from 'rxjs';

export type IdleSessionStatus = 'active' | 'warning' | 'expired';

export interface IdleSessionState {
    status: IdleSessionStatus;
    logoutAt: number | null;
}

@Injectable({
    providedIn: 'root',
})
export class IdleService {
    static readonly LAST_ACTIVITY_KEY = 'boss.lastActivityAt';
    static readonly LOGOUT_EVENT_KEY = 'boss.idleLogoutAt';

    readonly idleDurationMs = 30 * 60 * 1000;
    readonly warningDurationMs = 5 * 60 * 1000;

    private readonly _document = inject(DOCUMENT);
    private readonly _ngZone = inject(NgZone);
    private readonly _state = new BehaviorSubject<IdleSessionState>({
        status: 'active',
        logoutAt: null,
    });
    private readonly _stop$ = new Subject<void>();
    private _clockSubscription?: Subscription;
    private _watching = false;

    readonly state$ = this._state
        .asObservable()
        .pipe(
            distinctUntilChanged(
                (previous, current) =>
                    previous.status === current.status &&
                    previous.logoutAt === current.logoutAt
            )
        );

    get logoutAt(): number | null {
        return this._state.value.logoutAt;
    }

    startWatching(): void {
        if (this._watching) {
            this.evaluate();
            return;
        }

        this._watching = true;

        if (!this.getLastActivity()) {
            this.storeActivity(Date.now());
        }

        this._ngZone.runOutsideAngular(() => {
            const activityEvents = merge(
                fromEvent(this._document, 'pointerdown'),
                fromEvent(this._document, 'keydown'),
                fromEvent(this._document, 'mousemove'),
                fromEvent(this._document, 'touchstart', { passive: true }),
                fromEvent(this._document, 'scroll', {
                    capture: true,
                    passive: true,
                })
            );

            activityEvents
                .pipe(throttleTime(1000), takeUntil(this._stop$))
                .subscribe(() => {
                    if (this._state.value.status === 'active') {
                        this.recordActivity();
                    }
                });

            merge(
                fromEvent(window, 'focus'),
                fromEvent(this._document, 'visibilitychange')
            )
                .pipe(takeUntil(this._stop$))
                .subscribe(() => this.evaluate());

            fromEvent<StorageEvent>(window, 'storage')
                .pipe(
                    map((event) => event.key),
                    takeUntil(this._stop$)
                )
                .subscribe((key) => {
                    if (
                        key === IdleService.LAST_ACTIVITY_KEY ||
                        key === IdleService.LOGOUT_EVENT_KEY ||
                        key === 'accessToken'
                    ) {
                        this.evaluate();
                    }
                });

            this._clockSubscription = interval(1000)
                .pipe(takeUntil(this._stop$))
                .subscribe(() => this.evaluate());
        });

        this.evaluate();
    }

    stopWatching(): void {
        if (!this._watching) {
            return;
        }

        this._watching = false;
        this._stop$.next();
        this._clockSubscription?.unsubscribe();
        this._clockSubscription = undefined;
        this._state.next({ status: 'active', logoutAt: null });
    }

    confirmActivity(): void {
        this.storeActivity(Date.now());
        localStorage.removeItem(IdleService.LOGOUT_EVENT_KEY);
        this.evaluate();
    }

    expireSession(): void {
        localStorage.setItem(
            IdleService.LOGOUT_EVENT_KEY,
            Date.now().toString()
        );
        this._state.next({ status: 'expired', logoutAt: Date.now() });
    }

    clearSession(): void {
        localStorage.removeItem(IdleService.LAST_ACTIVITY_KEY);
        // Keep the logout marker until the next successful sign-in. This lets
        // suspended/background tabs detect the logout when they resume.
        this.stopWatching();
    }

    private recordActivity(): void {
        this.storeActivity(Date.now());
    }

    private storeActivity(timestamp: number): void {
        localStorage.setItem(
            IdleService.LAST_ACTIVITY_KEY,
            timestamp.toString()
        );
    }

    private getLastActivity(): number | null {
        const storedValue = Number(
            localStorage.getItem(IdleService.LAST_ACTIVITY_KEY)
        );

        return Number.isFinite(storedValue) && storedValue > 0
            ? storedValue
            : null;
    }

    private evaluate(): void {
        if (!this._watching) {
            return;
        }

        if (!localStorage.getItem('accessToken')) {
            this._ngZone.run(() =>
                this._state.next({
                    status: 'expired',
                    logoutAt: Date.now(),
                })
            );
            return;
        }

        const forcedLogoutAt = Number(
            localStorage.getItem(IdleService.LOGOUT_EVENT_KEY)
        );

        if (Number.isFinite(forcedLogoutAt) && forcedLogoutAt > 0) {
            this._ngZone.run(() =>
                this._state.next({
                    status: 'expired',
                    logoutAt: forcedLogoutAt,
                })
            );
            return;
        }

        const lastActivity = this.getLastActivity();

        if (!lastActivity) {
            this.storeActivity(Date.now());
            return;
        }

        const logoutAt =
            lastActivity + this.idleDurationMs + this.warningDurationMs;
        const now = Date.now();
        const nextState: IdleSessionState =
            now >= logoutAt
                ? { status: 'expired', logoutAt }
                : now >= lastActivity + this.idleDurationMs
                  ? { status: 'warning', logoutAt }
                  : { status: 'active', logoutAt: null };

        this._ngZone.run(() => this._state.next(nextState));
    }
}
